import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

// Connects to data-controller="photo-upload"
export default class extends Controller {
  static targets = ["input", "preview", "cropArea", "camera", "video", "submitBtn", "currentPhoto", "form"]
  static values = {
    url: String,          // Upload URL
    method: String,       // HTTP method (POST)
    aspectRatio: Number,  // 1 for square, 4/3 for landscape
    maxSize: Number       // Max file size in MB
  }

  connect() {
    console.log("Photo upload controller connected")
    this.cropper = null
    this.stream = null

    // Default values
    if (!this.aspectRatioValue) this.aspectRatioValue = 1  // Square by default
    if (!this.maxSizeValue) this.maxSizeValue = 15         // 15MB by default for modern phones
  }

  disconnect() {
    this.destroyCropper()
    this.stopCamera()
  }

  // Trigger file input click
  selectFile() {
    this.inputTarget.click()
  }

  // Preview selected file
  previewFile(event) {
    const file = event.target.files[0]

    if (!file) return

    // Validate file type
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp']
    if (!validTypes.includes(file.type)) {
      this.showToast("Format non supporté. Utilisez PNG, JPEG ou WebP.", "error")
      return
    }

    // Validate file size
    const maxBytes = this.maxSizeValue * 1024 * 1024
    if (file.size > maxBytes) {
      this.showToast(`Fichier trop volumineux. Taille maximum: ${this.maxSizeValue}MB`, "error")
      return
    }

    // Read and display preview
    const reader = new FileReader()
    reader.onload = (e) => {
      this.previewTarget.src = e.target.result
      this.showCropInterface()
    }
    reader.readAsDataURL(file)
  }

  // Show crop interface with Cropper.js
  showCropInterface() {
    this.destroyCropper()

    // Hide current photo, show crop area
    if (this.hasCurrentPhotoTarget) {
      this.currentPhotoTarget.classList.add('hidden')
    }
    this.cropAreaTarget.classList.remove('hidden')

    // Scroll to crop area on mobile for better visibility
    setTimeout(() => {
      this.cropAreaTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }, 100)

    // Initialize Cropper.js
    this.cropper = new Cropper(this.previewTarget, {
      aspectRatio: this.aspectRatioValue,
      viewMode: 2,
      guides: true,
      center: true,
      highlight: true,
      autoCropArea: 0.8,
      responsive: true,
      restore: false,
      checkOrientation: true,
      scalable: true,
      zoomable: true,
      cropBoxResizable: true,
      toggleDragModeOnDblclick: false
    })

    console.log("Cropper initialized with aspect ratio:", this.aspectRatioValue)
    this.showToast("Ajustez votre photo, puis cliquez sur 'Valider et uploader'", "info")
  }

  // Camera capture
  async captureFromCamera() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      })

      // Show camera interface
      if (this.hasCurrentPhotoTarget) {
        this.currentPhotoTarget.classList.add('hidden')
      }
      this.cameraTarget.classList.remove('hidden')

      // Stream to video element
      this.videoTarget.srcObject = this.stream
      this.videoTarget.play()

      console.log("Camera started")
    } catch (error) {
      console.error("Camera access error:", error)
      alert("Impossible d'accéder à la caméra. Vérifiez les permissions.")
    }
  }

  // Capture snapshot from camera
  captureSnapshot() {
    if (!this.stream) return

    // Create canvas and capture frame
    const canvas = document.createElement('canvas')
    canvas.width = this.videoTarget.videoWidth
    canvas.height = this.videoTarget.videoHeight

    const context = canvas.getContext('2d')
    context.drawImage(this.videoTarget, 0, 0, canvas.width, canvas.height)

    // Stop camera
    this.stopCamera()

    // Convert to data URL and show in cropper
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
    this.previewTarget.src = dataUrl
    this.showCropInterface()

    console.log("Snapshot captured")
  }

  // Stop camera stream
  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    if (this.hasCameraTarget) {
      this.cameraTarget.classList.add('hidden')
    }
    console.log("Camera stopped")
  }

  // Submit cropped image
  async submitCroppedImage(event) {
    event.preventDefault()

    if (!this.cropper) {
      this.showToast("Aucune image à uploader", "error")
      return
    }

    // Show loading state
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.innerHTML = `
        <svg class="animate-spin h-5 w-5 inline mr-2" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Compression et upload...
      `
    }

    try {
      // Get cropped canvas with optimized size for web
      const canvas = this.cropper.getCroppedCanvas({
        width: this.aspectRatioValue === 1 ? 800 : 1200,
        height: this.aspectRatioValue === 1 ? 800 : 900,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      })

      // Convert to blob with progressive quality reduction if needed
      let blob = await this.compressImage(canvas, 0.90)

      // If still too large (>2MB), reduce quality
      if (blob.size > 2 * 1024 * 1024) {
        blob = await this.compressImage(canvas, 0.80)
      }

      // If STILL too large, reduce quality more
      if (blob.size > 2 * 1024 * 1024) {
        blob = await this.compressImage(canvas, 0.70)
      }

      console.log(`Final image size: ${(blob.size / 1024 / 1024).toFixed(2)}MB`)

      // Create form data
      const formData = new FormData()
      const fieldName = this.aspectRatioValue === 1 ? 'avatar' : 'photo'
      formData.append(fieldName, blob, 'upload.jpg')

      // Get CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      // Submit via fetch
      const response = await fetch(this.urlValue, {
        method: this.methodValue || 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
        },
        credentials: 'same-origin'
      })

      if (response.ok) {
        console.log("Upload successful")
        this.showToast("Photo uploadée ! Traitement en cours...", "success")

        // Wait a bit for the toast, then reload
        setTimeout(() => {
          // Let Turbo handle the response (redirect)
          const contentType = response.headers.get('content-type')
          if (contentType && contentType.includes('turbo-stream')) {
            response.text().then(text => Turbo.renderStreamMessage(text))
          } else {
            // Standard redirect - this will show the processing placeholder
            window.location.reload()
          }
        }, 800)

        // After redirect, schedule another reload to show the processed image
        // This gives the background job time to generate variants
        setTimeout(() => {
          console.log("Reloading to show processed avatar...")
          window.location.reload()
        }, 5000)  // 5 seconds should be enough for variant generation
      } else {
        throw new Error(`Upload failed: ${response.statusText}`)
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.showToast("Erreur lors de l'upload. Vérifiez votre connexion et réessayez.", "error")

      // Reset button
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.innerHTML = "✅ Valider et uploader"
      }
    }
  }

  // Compress image to blob with specified quality
  async compressImage(canvas, quality) {
    return new Promise(resolve => {
      canvas.toBlob(resolve, 'image/jpeg', quality)
    })
  }

  // Show toast notification
  showToast(message, type = "info") {
    // Remove existing toasts
    const existingToast = document.querySelector('.photo-upload-toast')
    if (existingToast) {
      existingToast.remove()
    }

    // Create toast
    const toast = document.createElement('div')
    toast.className = 'photo-upload-toast fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-sm w-full mx-auto'

    const bgColor = type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-blue-600'

    toast.innerHTML = `
      <div class="${bgColor} text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-slideDown">
        ${type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}
        <span class="font-semibold">${message}</span>
      </div>
    `

    document.body.appendChild(toast)

    // Auto-remove after 3 seconds
    setTimeout(() => {
      toast.classList.add('animate-slideUp')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  // Cancel crop and reset
  cancelCrop() {
    this.destroyCropper()
    this.stopCamera()

    // Hide crop area, show current photo
    this.cropAreaTarget.classList.add('hidden')
    if (this.hasCurrentPhotoTarget) {
      this.currentPhotoTarget.classList.remove('hidden')
    }

    // Reset file input
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }

    console.log("Crop cancelled")
  }

  // Destroy cropper instance
  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }
}
